import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FieldErrorDto,
  NonAwardReason,
  useGetCancellationScopeQuery,
  useSubmitNoWinnerNoticeMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { nonAwardReasons } from "./nonAwardReasons";
import { DoffinNoAnswerAlert } from "./DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal, isResultNoticeAlreadySent } from "./DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "./doffinNoAnswer";

interface Props {
  eventId: string;
  reason?: NonAwardReason;
  relaunch?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const NoWinnerNoticeModal = ({
  eventId,
  reason: givenReason,
  relaunch: givenRelaunch,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [pickedReason, setReason] = useState<NonAwardReason | "">(givenReason ?? "");
  const { data: scope, isLoading: scopeLoading, isError: scopeFailed } = useGetCancellationScopeQuery(
    isOpen ? { eventId } : skipToken,
  );
  const contradictedReasons = scope?.contradictedReasons ?? [];
  const reason = pickedReason !== "" && contradictedReasons.includes(pickedReason) ? "" : pickedReason;
  const removedCause =
    givenReason && contradictedReasons.includes(givenReason) && pickedReason === givenReason
      ? nonAwardReasons(t, []).find(([value]) => value === givenReason)?.[1]
      : undefined;
  const [relaunch, setRelaunch] = useState<boolean | null>(
    givenRelaunch ?? null,
  );
  const [submit, { isLoading }] = useSubmitNoWinnerNoticeMutation();

  const send = async () => {
    if (reason === "" || relaunch == null) return;
    setRefusal(null);
    setNoAnswer(null);
    try {
      await submit({
        sourcingEventId: eventId,
        submitNoWinnerNoticeRequest: { reason, relaunch },
      }).unwrap();
      toast({
        title: t("The result notice is sent to Doffin"),
        status: "success",
        position: "top-right",
      });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusal(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else if (isResultNoticeAlreadySent(e)) {
        trigger(e);
        onClose();
      } else trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("Announce the result on Doffin")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing="5">
            <Box>
              <Text fontWeight="semibold">{t("Outcome")}</Text>
              <Text>{t("No winner chosen, the competition is closed")}</Text>
            </Box>
            <FormControl isRequired>
              <FormLabel htmlFor="no-winner-reason">{t("Cause")}</FormLabel>
              {removedCause && (
                <Text fontSize="sm" color="smError" pb="2">
                  {t("The cause given at the cancellation, «{{cause}}», does not match the tenders on this procurement. Choose another cause.", { cause: removedCause })}
                </Text>
              )}
              {scopeFailed && (
                <Text fontSize="sm" color="smError" pb="2">
                  {t("Could not check the causes against the tenders on this procurement. A cause that does not match them is refused when you send.")}
                </Text>
              )}
              <Select
                id="no-winner-reason"
                placeholder={t("Choose the cause") ?? undefined}
                backgroundColor="smBackground"
                isDisabled={scopeLoading}
                value={reason}
                onChange={(e) => setReason(e.target.value as NonAwardReason)}
              >
                {nonAwardReasons(t, contradictedReasons).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                {t("Will the competition be announced again?")}
              </FormLabel>
              <RadioGroup
                value={relaunch == null ? "" : String(relaunch)}
                onChange={(value) => setRelaunch(value === "true")}
              >
                <Stack direction="row" spacing="6">
                  <Radio value="true">{t("Yes")}</Radio>
                  <Radio value="false">{t("No")}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            {refusal && <DoffinRefusalAlert breaches={refusal} />}
            {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} />}
          </Stack>
          <Flex justify="end" pt="6">
            <Button
              colorScheme="teal"
              isDisabled={reason === "" || relaunch == null || noAnswer === "whileSending" || scopeLoading}
              isLoading={isLoading}
              onClick={send}
            >
              {t("Announce on Doffin")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
